import { useEmbarcacaoComercial } from "@/modules/embarcacoes";
import type { EmbarcacaoComercial, EmbarcacaoComercialField } from "@/modules/embarcacoes";
import type { EmptyStateContent } from "@/screens/control-panel/components/empty-state";
import {
  DetailField,
  DetailSection,
  DetailSectionFields,
  DetailSectionState,
} from "../detail-section";
import { formatDetailDate, formatDetailDateOrText } from "../embarcacao-detail-format";
import type { EmbarcacaoDetailSectionProps } from "../embarcacao-detail-section";

export const COMERCIAL_ID = "comercial";
export const COMERCIAL_TITLE = "Comercial";
export const COMERCIAL_LOADING_MESSAGE = "Carregando as informações Comerciais da embarcação...";

/** Padrão global de estado vazio do Hub para a seção sem informações (AC4). */
const COMERCIAL_EMPTY_STATE: EmptyStateContent = {
  title: "Nenhuma informação comercial disponível",
  description:
    "Assim que houver dados comerciais para esta embarcação, eles aparecerão nesta seção.",
};

const TEST_ID = "embarcacao-comercial";

interface ComercialFieldDefinition {
  readonly key: EmbarcacaoComercialField;
  readonly label: string;
  /** Tratamento de apresentação do valor; sem ele o valor vai como recebido. */
  readonly format?: (value: string) => string;
}

/**
 * Os 5 campos da seção, na ordem e nas colunas do protótipo (AC1/AC3). O grid
 * os distribui em duas colunas, preenchendo a linha da esquerda para a direita:
 * Cliente Atual com Observações, Início com Fim do Contrato e a Extensão do
 * Contrato sozinha na última linha.
 */
export const COMERCIAL_FIELDS: ComercialFieldDefinition[] = [
  { key: "clienteAtual", label: "Cliente Atual" },
  { key: "observacoes", label: "Observações" },
  { key: "inicioContrato", label: "Início de Contrato", format: formatDetailDate },
  { key: "fimContrato", label: "Fim do Contrato", format: formatDetailDate },
  // Data no contrato, mas guardada na base conforme o valor disponível.
  { key: "extensaoContrato", label: "Extensão do Contrato", format: formatDetailDateOrText },
];

/** Valor do campo pronto para a tela; `null` segue como campo sem preenchimento. */
function fieldValue(
  comercial: EmbarcacaoComercial,
  { key, format }: ComercialFieldDefinition,
): string | null {
  const value = comercial[key];
  if (value === null) return null;

  return format ? format(value) : value;
}

/**
 * Seção Comercial do detalhamento da embarcação (US-1792).
 *
 * Exclusivamente de leitura (AC5): nenhum botão ou campo de cadastro, edição
 * ou exclusão. Os valores são apresentados como recebidos da fonte, sem
 * cálculo nem derivação — os únicos tratamentos são o formato de data do Hub e
 * o "Campo sem preenchimento" para valor ausente (AC3/AC4).
 */
export function ComercialSection({ embarcacaoId, onReauthenticate }: EmbarcacaoDetailSectionProps) {
  const { status, comercial, errorMessage, reload } = useEmbarcacaoComercial(embarcacaoId);

  return (
    <DetailSection
      id={COMERCIAL_ID}
      title={COMERCIAL_TITLE}
      // Só há data de atualização a exibir quando a seção tem dados.
      ultimaAtualizacao={comercial ? comercial.ultimaAtualizacao : undefined}
      testId={TEST_ID}
    >
      {comercial ? (
        <DetailSectionFields testId={`${TEST_ID}-fields`}>
          {COMERCIAL_FIELDS.map((field) => (
            <DetailField
              key={field.key}
              label={field.label}
              value={fieldValue(comercial, field)}
              testId={`${TEST_ID}-${field.key}`}
            />
          ))}
        </DetailSectionFields>
      ) : (
        <DetailSectionState
          // `success` sempre traz os campos: aqui só chegam carregamento e exceções.
          status={status === "success" ? "error" : status}
          loadingMessage={COMERCIAL_LOADING_MESSAGE}
          errorMessage={errorMessage}
          emptyContent={COMERCIAL_EMPTY_STATE}
          onReauthenticate={onReauthenticate}
          onRetry={reload}
          testId={TEST_ID}
        />
      )}
    </DetailSection>
  );
}
